import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { StandingEntry } from "./useStandingsCache";

interface UseStandingsCacheGroupedOptions {
  leagueApiId?: number;
  season?: number;
}

interface GroupedStandingsResult {
  groupedStandings: StandingEntry[][];
  lastUpdated: string | null;
  leagueInfo: any;
}

async function fetchGroupedStandingsData(
  leagueApiId: number,
  season: number
): Promise<GroupedStandingsResult> {
  // First get the league UUID from api_football_id
  const { data: leagueData, error: leagueError } = await supabase
    .from('leagues')
    .select('id, name, country, logo_url, api_football_id')
    .eq('api_football_id', leagueApiId)
    .single();

  if (leagueError) {
    console.error('Error fetching league:', leagueError);
    throw leagueError;
  }

  if (!leagueData) {
    console.warn('League not found for api_football_id:', leagueApiId);
    return { groupedStandings: [], lastUpdated: null, leagueInfo: null };
  }

  // Now fetch standings cache
  const { data, error } = await supabase
    .from('standings_cache')
    .select('*')
    .eq('league_id', leagueData.id)
    .eq('season', season)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching standings cache:', error);
    throw error;
  }

  if (data?.data_json && typeof data.data_json === 'object') {
    const jsonData = data.data_json as any;
    const standingsData = jsonData?.response?.[0]?.league?.standings;
    
    if (standingsData && Array.isArray(standingsData)) {
      // Check if first element is an array (grouped standings)
      if (Array.isArray(standingsData[0]) && standingsData[0].length > 0) {
        // Already grouped
        return {
          groupedStandings: standingsData as StandingEntry[][],
          lastUpdated: data.updated_at,
          leagueInfo: leagueData
        };
      } else {
        // Single group, wrap in array
        return {
          groupedStandings: [standingsData as StandingEntry[]],
          lastUpdated: data.updated_at,
          leagueInfo: leagueData
        };
      }
    }
  }

  return { groupedStandings: [], lastUpdated: null, leagueInfo: leagueData };
}

export function useStandingsCacheGrouped(options: UseStandingsCacheGroupedOptions = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { leagueApiId, season = new Date().getFullYear() } = options;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['standings-cache-grouped', leagueApiId, season],
    queryFn: () => fetchGroupedStandingsData(leagueApiId!, season),
    enabled: !!leagueApiId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

  // Show error toast
  if (error) {
    toast({
      title: "Error",
      description: "Failed to load standings",
      variant: "destructive"
    });
  }

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['standings-cache-grouped', leagueApiId, season] });
  };

  return {
    groupedStandings: data?.groupedStandings ?? [],
    loading: isLoading,
    lastUpdated: data?.lastUpdated ?? null,
    leagueInfo: data?.leagueInfo ?? null,
    refresh,
    refetch
  };
}
